// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Dataset } from '../data';
import type { Incident } from '../types';
import {
  OUTCOME_COLOUR,
  OUTCOME_LABEL,
  OUTCOME_ORDER,
  speciesInfo,
  activityLabel,
  stateName,
  stateColour,
} from '../domain';
import { outcomeCounts, speciesStats, activityStats, stateStats, fatalPct } from '../analysis';
import { stackedBarHtml, type DonutSeg } from './charts';
import { escapeHtml, formatNumber, formatPercent, formatIncidentDate, formatTime, titleCase } from '../utils/format';

type ViewNav = (view: string) => void;

/**
 * One slide-in drawer, reused for incident / state / species detail. Detached
 * from the DOM when closed: a panel parked at translateX(100%) is still a real
 * box on iOS Safari and causes phantom horizontal scroll, which overflow-x:clip
 * neither fixes nor lets scrollWidth detect. Removing it from the DOM does.
 */
export function createDrawers(data: Dataset, goTo: ViewNav) {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  const drawer = document.createElement('div');
  drawer.className = 'drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'true');
  const closeBtn = document.createElement('button');
  closeBtn.className = 'modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '✕';

  let mounted = false;
  let removeTimer: number | undefined;

  const mount = () => {
    if (mounted) return;
    window.clearTimeout(removeTimer);
    document.body.append(overlay, drawer);
    // Force reflow so the open transition runs from the detached start state.
    void drawer.offsetWidth;
    mounted = true;
  };

  const close = () => {
    overlay.classList.remove('open');
    drawer.classList.remove('open');
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    removeTimer = window.setTimeout(() => {
      overlay.remove();
      drawer.remove();
      mounted = false;
    }, 300);
  };

  const openHtml = (hash: string, head: string, body: string, after?: (root: HTMLElement) => void) => {
    mount();
    drawer.innerHTML = `<div class="drawer-head">${head}</div><div class="drawer-body">${body}</div>`;
    drawer.appendChild(closeBtn);
    overlay.classList.add('open');
    drawer.classList.add('open');
    drawer.scrollTop = 0;
    closeBtn.focus();
    if (hash) history.replaceState(null, '', `#${hash}`);
    after?.(drawer);
  };

  overlay.addEventListener('click', close);
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mounted && drawer.classList.contains('open')) close();
  });

  // ── Incident ──────────────────────────────────────────────────────────────
  function openIncident(id: number): void {
    const it = data.byId.get(id);
    if (!it) return;
    const sp = speciesInfo(it.sp);
    const head = `
      <div class="drawer-eyebrow" style="color:${OUTCOME_COLOUR[it.out]}">${OUTCOME_LABEL[it.out]} incident</div>
      <div class="drawer-title">${escapeHtml(it.loc ? titleCase(it.loc) : 'Location not recorded')}</div>
      <div class="drawer-sub">
        ${escapeHtml(formatIncidentDate(it.y, it.m, it.d))}
        ${it.st ? ` · <span class="pill" style="background:${stateColour(it.st)}">${escapeHtml(it.st)}</span>` : ''}
      </div>`;

    const fact = (label: string, value: string) => `<div class="kv"><div class="kv-label">${label}</div><div class="kv-value" style="font-size:var(--font-size-base)">${value}</div></div>`;
    const sameLoc = it.loc ? data.incidents.filter((x) => x.loc && x.loc.toLowerCase() === it.loc.toLowerCase()) : [];

    const body = `
      <div class="drawer-section">
        <div class="kv-grid">
          ${fact('Outcome', `<span style="color:${OUTCOME_COLOUR[it.out]}">${OUTCOME_LABEL[it.out]}</span>`)}
          ${fact('Nature', it.prov == null ? 'Unknown' : it.prov ? 'Provoked' : 'Unprovoked')}
          ${fact('Activity', escapeHtml(activityLabel(it.act)))}
          ${fact('Time', it.time != null ? `${formatTime(it.time)}${it.pod ? ` (${it.pod})` : ''}` : '—')}
        </div>
      </div>
      <div class="drawer-section">
        <h3>Shark</h3>
        <div class="species-head">
          <span class="legend-swatch" style="background:${sp.colour};width:16px;height:16px"></span>
          <strong>${escapeHtml(sp.label)}</strong>
          ${sp.sci ? `<em class="sci">${escapeHtml(sp.sci)}</em>` : ''}
          ${sp.big3 ? '<span class="flag">one of the three</span>' : ''}
        </div>
        <p class="drawer-note">${escapeHtml(sp.note)}</p>
        ${it.len != null ? `<p class="drawer-note">Estimated length: <strong>${it.len.toFixed(1)} m</strong>.</p>` : ''}
      </div>
      <div class="drawer-section">
        <h3>The person</h3>
        <div class="kv-grid">
          ${fact('Sex', it.sex === 'unknown' ? '—' : it.sex[0].toUpperCase() + it.sex.slice(1))}
          ${fact('Age', it.age != null ? String(it.age) : '—')}
          ${fact('Injury site', it.injloc ? escapeHtml(titleCase(it.injloc)) : '—')}
          ${fact('Injury', it.sev ? escapeHtml(it.sev) : '—')}
        </div>
      </div>
      ${
        sameLoc.length > 1
          ? `<div class="drawer-section"><h3>At this location</h3>
              <p class="drawer-note">${sameLoc.length} recorded incidents at ${escapeHtml(titleCase(it.loc))}, of which ${sameLoc.filter((x) => x.out === 'fatal').length} were fatal.</p>
              <div class="mini-list">${sameLoc
                .filter((x) => x.id !== it.id)
                .slice(0, 6)
                .map((x) => incidentRow(x))
                .join('')}</div></div>`
          : ''
      }
      <div class="drawer-section">
        <button class="chip" data-open-species="${escapeHtml(it.sp)}">About the ${escapeHtml(sp.label.toLowerCase())} →</button>
        ${it.st ? `<button class="chip" data-open-state="${escapeHtml(it.st)}">${escapeHtml(stateName(it.st))} overview →</button>` : ''}
      </div>`;

    openHtml(`i=${id}`, head, body, wireDrawerLinks);
  }

  // ── State ─────────────────────────────────────────────────────────────────
  function openState(st: string): void {
    const inState = data.incidents.filter((x) => x.st === st);
    if (!inState.length) return;
    const c = outcomeCounts(inState);
    const rows = stateStats(data.incidents);
    const rank = rows.findIndex((r) => r.st === st);
    const me = rows[rank];
    const sp = speciesStats(inState).filter((s) => s.key !== 'unidentified').slice(0, 5);
    const acts = activityStats(inState).sort((a, b) => b.total - a.total).slice(0, 5);
    const recent = [...inState].filter((x) => x.y != null).sort((a, b) => (b.y! - a.y!) || (b.m ?? 0) - (a.m ?? 0)).slice(0, 6);

    const head = `
      <div class="drawer-eyebrow" style="color:${stateColour(st)}">State overview</div>
      <div class="drawer-title" style="font-family:var(--font-sans)">${escapeHtml(stateName(st))}</div>
      <div class="drawer-sub">${formatNumber(inState.length)} recorded incidents · ${formatNumber(c.fatal)} fatal</div>`;

    const body = `
      <div class="drawer-section">
        <div class="kv-grid">
          <div class="kv"><div class="kv-label">Incidents</div><div class="kv-value">${formatNumber(inState.length)}</div></div>
          <div class="kv"><div class="kv-label">Fatal</div><div class="kv-value" style="color:${OUTCOME_COLOUR.fatal}">${formatNumber(c.fatal)}</div></div>
          <div class="kv"><div class="kv-label">Fatal rate</div><div class="kv-value">${formatPercent(fatalPct(c))}</div></div>
          <div class="kv"><div class="kv-label">Per million residents</div><div class="kv-value">${me?.perMillion != null ? me.perMillion.toFixed(0) : '—'}</div></div>
        </div>
        <p class="drawer-note" style="margin-top:var(--space-sm)">Ranked <strong>#${rank + 1}</strong> of ${rows.length} states by total incidents.</p>
      </div>
      <div class="drawer-section"><h3>Outcome split</h3>${outcomeBar(inState)}${outcomeLegend()}</div>
      <div class="drawer-section"><h3>Sharks most involved</h3>${miniRanked(sp.map((s) => ({ label: s.label, value: s.total, colour: speciesInfo(s.key).colour })))}</div>
      <div class="drawer-section"><h3>What people were doing</h3>${miniRanked(acts.map((a) => ({ label: activityLabel(a.key), value: a.total, colour: '#2b8994' })))}</div>
      <div class="drawer-section"><h3>Most recent</h3><div class="mini-list">${recent.map((x) => incidentRow(x)).join('')}</div></div>`;

    openHtml(`s=${st}`, head, body, wireDrawerLinks);
  }

  // ── Species ───────────────────────────────────────────────────────────────
  function openSpecies(key: string): void {
    const info = speciesInfo(key);
    const inSp = data.incidents.filter((x) => x.sp === key);
    if (!inSp.length) return;
    const c = outcomeCounts(inSp);
    const allRows = speciesStats(data.incidents);
    const rank = allRows.findIndex((r) => r.key === key);
    const byState = stateStats(inSp).slice(0, 5);
    const acts = activityStats(inSp).sort((a, b) => b.total - a.total).slice(0, 5);
    const recent = [...inSp].filter((x) => x.y != null).sort((a, b) => b.y! - a.y!).slice(0, 6);

    const head = `
      <div class="drawer-eyebrow" style="color:${info.colour}">Species</div>
      <div class="drawer-title" style="font-family:var(--font-sans)">${escapeHtml(info.label)}</div>
      <div class="drawer-sub">${info.sci ? `<em>${escapeHtml(info.sci)}</em> · ` : ''}${formatNumber(inSp.length)} incidents · ${formatNumber(c.fatal)} fatal</div>`;

    const body = `
      <div class="drawer-section"><p class="drawer-note">${escapeHtml(info.note)}</p></div>
      <div class="drawer-section">
        <div class="kv-grid">
          <div class="kv"><div class="kv-label">Incidents</div><div class="kv-value">${formatNumber(inSp.length)}</div></div>
          <div class="kv"><div class="kv-label">Fatal</div><div class="kv-value" style="color:${OUTCOME_COLOUR.fatal}">${formatNumber(c.fatal)}</div></div>
          <div class="kv"><div class="kv-label">Fatal rate</div><div class="kv-value">${formatPercent(fatalPct(c))}</div></div>
          <div class="kv"><div class="kv-label">Rank by count</div><div class="kv-value">#${rank + 1}</div></div>
        </div>
      </div>
      <div class="drawer-section"><h3>Outcome split</h3>${outcomeBar(inSp)}${outcomeLegend()}</div>
      <div class="drawer-section"><h3>Where</h3>${miniRanked(byState.map((s) => ({ label: stateName(s.st), value: s.total, colour: stateColour(s.st) })))}</div>
      <div class="drawer-section"><h3>During what activity</h3>${miniRanked(acts.map((a) => ({ label: activityLabel(a.key), value: a.total, colour: '#2b8994' })))}</div>
      <div class="drawer-section"><h3>Most recent</h3><div class="mini-list">${recent.map((x) => incidentRow(x)).join('')}</div></div>`;

    openHtml(`sp=${key}`, head, body, wireDrawerLinks);
  }

  // Shared little renderers -----------------------------------------------------
  function incidentRow(x: Incident): string {
    const sp = speciesInfo(x.sp);
    return `<button class="mini-row" data-open-incident="${x.id}">
      <span class="mini-dot" style="background:${OUTCOME_COLOUR[x.out]}"></span>
      <span class="mini-main">${escapeHtml(x.loc ? titleCase(x.loc) : 'Unknown location')}</span>
      <span class="mini-meta">${x.y ?? '—'} · ${escapeHtml(sp.label)}</span>
    </button>`;
  }

  function outcomeBar(list: Incident[]): string {
    const c = outcomeCounts(list);
    const segs: DonutSeg[] = OUTCOME_ORDER.map((o) => ({ value: c[o], colour: OUTCOME_COLOUR[o], label: OUTCOME_LABEL[o] }));
    return stackedBarHtml(segs, { tall: true });
  }
  function outcomeLegend(): string {
    return `<div class="legend" style="margin-top:var(--space-sm)">${OUTCOME_ORDER.map((o) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OUTCOME_COLOUR[o]}"></span>${OUTCOME_LABEL[o]}</span>`).join('')}</div>`;
  }
  function miniRanked(items: Array<{ label: string; value: number; colour: string }>): string {
    if (!items.length) return '<p class="empty" style="padding:var(--space-md)">No data.</p>';
    const max = Math.max(...items.map((i) => i.value), 1);
    return `<div class="mini-ranked">${items
      .map(
        (i) => `<div class="mr-row"><span class="mr-label">${escapeHtml(i.label)}</span><span class="mr-track"><span class="mr-fill" style="width:${((i.value / max) * 100).toFixed(1)}%;background:${i.colour}"></span></span><span class="mr-val">${formatNumber(i.value)}</span></div>`,
      )
      .join('')}</div>`;
  }
  function wireDrawerLinks(root: HTMLElement): void {
    root.querySelectorAll('[data-open-incident]').forEach((b) =>
      b.addEventListener('click', () => openIncident(Number(b.getAttribute('data-open-incident')))),
    );
    root.querySelectorAll('[data-open-species]').forEach((b) =>
      b.addEventListener('click', () => openSpecies(b.getAttribute('data-open-species') as string)),
    );
    root.querySelectorAll('[data-open-state]').forEach((b) =>
      b.addEventListener('click', () => openState(b.getAttribute('data-open-state') as string)),
    );
    root.querySelectorAll('[data-goto]').forEach((b) =>
      b.addEventListener('click', () => {
        close();
        goTo(b.getAttribute('data-goto') as string);
      }),
    );
  }

  return { openIncident, openState, openSpecies, close };
}
